import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import api, { shortfallApi } from '../services/api';
import DesignCuttingRecordDialog from './DesignCuttingRecordDialog';

interface Props {
  open: boolean;
  designNo: string | null;
  onClose: () => void;
  onOpenCuttingForm?: (designNo: string, readOnly: boolean) => void;
}

interface WorkerAssignment {
  id: number;
  design_no: string;
  garment_part: string;
  size: string;
  quantity_issued: number;
  receives?: Array<{
    quantity_received: number;
    defect_quantity: number;
  }>;
}

const sizeOrder = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'];

const DispatchedDetailsDialog: React.FC<Props> = ({ open, designNo, onClose, onOpenCuttingForm }) => {
  const [loading, setLoading] = useState(false);
  const [cuttings, setCuttings] = useState<any[]>([]);
  const [assignments, setAssignments] = useState<WorkerAssignment[]>([]);
  const [jobWorks, setJobWorks] = useState<any[]>([]);
  const [showCuttingRecordDialog, setShowCuttingRecordDialog] = useState(false);
  const [shortfallSummary, setShortfallSummary] = useState<Record<string, { open: number; closed: number }>>({});

  useEffect(() => {
    if (!open || !designNo) return;
    const load = async () => {
      try {
        setLoading(true);
        const [dcRes, waRes, jwRes] = await Promise.all([
          api.get('/design-cutting'),
          api.get('/worker-assignments'),
          api.get(`/job-works/by-design/${designNo}`)
        ]);
        
        // Fetch shortfall summary separately
        let shortfallData = {};
        try {
          shortfallData = await shortfallApi.summary(designNo) || {};
        } catch (e) {
          console.warn('Failed to fetch shortfall summary:', e);
        }

        setCuttings((dcRes.data || []).filter((d: any) => d.design_no === designNo && d.status === 'COMPLETED'));
        // Filter to only STITCHING job type and not refinish assignments
        setAssignments((waRes.data || []).filter((w: WorkerAssignment) => 
          w.design_no === designNo && 
          (w as any).jobType === 'STITCHING' &&
          !(w as any).is_refinish_job
        ));
        setJobWorks(jwRes.data || []);
        setShortfallSummary(shortfallData);
      } catch (e) {
        setCuttings([]);
        setAssignments([]);
        setJobWorks([]);
        setShortfallSummary({});
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open, designNo]);

  const parts = useMemo(() => Array.from(new Set(cuttings.map(c => (c.garment_part || '').toUpperCase()))), [cuttings]);

  const programByPart = useMemo(() => {
    const map: { [part: string]: { [size: string]: number } } = {};
    
    // Fix double counting: Group by garment_part and batch_number, use only latest batch
    const latestByPart: { [part: string]: any } = {};
    cuttings.forEach((c: any) => {
      const gp = (c.garment_part || '').toUpperCase();
      const currentBatch = c.batch_number || 1;
      const existing = latestByPart[gp];
      
      // Prefer COMPLETED status, otherwise use latest batch number
      if (!existing || 
          (c.status === 'COMPLETED' && existing.status !== 'COMPLETED') ||
          (c.status === existing.status && currentBatch > (existing.batch_number || 1)) ||
          (c.status !== 'COMPLETED' && existing.status !== 'COMPLETED' && currentBatch > (existing.batch_number || 1))) {
        latestByPart[gp] = c;
      }
    });
    
    // Use only the latest batch record per garment part to avoid double counting
    Object.values(latestByPart).forEach((c: any) => {
      const gp = (c.garment_part || '').toUpperCase();
      if (!map[gp]) map[gp] = {};
      map[gp]['XS'] = c.xs_pieces || 0;
      map[gp]['S'] = c.s_pieces || 0;
      map[gp]['M'] = c.m_pieces || 0;
      map[gp]['L'] = c.l_pieces || 0;
      map[gp]['XL'] = c.xl_pieces || 0;
      map[gp]['XXL'] = c.xxl_pieces || 0;
      map[gp]['3XL'] = c.three_xl_pieces || 0;
    });
    
    return map;
  }, [cuttings]);

  const issuedByPart = useMemo(() => {
    const map: { [part: string]: { [size: string]: number } } = {};
    assignments.forEach(a => {
      const gp = (a.garment_part || '').toUpperCase();
      const size = (a.size || '').toUpperCase();
      if (!map[gp]) map[gp] = {};
      map[gp][size] = (map[gp][size] || 0) + (a.quantity_issued || 0);
    });
    return map;
  }, [assignments]);

  const receivedByPart = useMemo(() => {
    const map: { [part: string]: { [size: string]: number } } = {};
    assignments.forEach(a => {
      const gp = (a.garment_part || '').toUpperCase();
      const size = (a.size || '').toUpperCase();
      if (!map[gp]) map[gp] = {};
      // Only count quantity_received, NOT defect_quantity
      // Defects are tracked separately and can be re-assigned later if fabric is found
      const totalRec = (a.receives || []).reduce((s, r) => s + (r.quantity_received || 0), 0);
      map[gp][size] = (map[gp][size] || 0) + totalRec;
    });
    return map;
  }, [assignments]);


  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        Dispatched Details - {designNo}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={() => {
              if (designNo && typeof (onOpenCuttingForm) === 'function') {
                onOpenCuttingForm(designNo, true);
                onClose();
              } else {
                setShowCuttingRecordDialog(true);
              }
            }}
          >
            Open Design Cutting Form
          </Button>
        </Box>
        <IconButton onClick={onClose} aria-label="close" sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {parts.map(part => {
              const sizes = sizeOrder.filter(s => (programByPart[part]?.[s] || 0) > 0);
              const totalProgram = sizes.reduce((s, sz) => s + (programByPart[part]?.[sz] || 0), 0);
              const totalIssued = sizes.reduce((s, sz) => s + (issuedByPart[part]?.[sz] || 0), 0);
              const totalReceived = sizes.reduce((s, sz) => s + (receivedByPart[part]?.[sz] || 0), 0);

              return (
                <Grid item xs={12} key={part}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Chip label={part} color="primary" />
                      <Typography variant="h6">Program Information & Worker Flow</Typography>
                    </Box>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Size</strong></TableCell>
                          <TableCell align="right"><strong>Cut (Program)</strong></TableCell>
                          <TableCell align="right"><strong>Issued</strong></TableCell>
                          <TableCell align="right"><strong>Received</strong></TableCell>
                          <TableCell align="right" sx={{ bgcolor: 'warning.light' }}><strong>Shortfall</strong></TableCell>
                          <TableCell align="right"><strong>Pending</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sizes.map(sz => {
                          const programQty = programByPart[part]?.[sz] || 0;
                          const issuedQty = issuedByPart[part]?.[sz] || 0;
                          const receivedQty = receivedByPart[part]?.[sz] || 0;
                          const closedShort = shortfallSummary[`${part}|${sz}`]?.closed || 0;
                          // Calculate pending: Cut - Received - ClosedShortfall
                          const pending = Math.max(0, programQty - receivedQty - closedShort);
                          return (
                            <TableRow key={`${part}-${sz}`}>
                              <TableCell>{sz}</TableCell>
                              <TableCell align="right">{programQty}</TableCell>
                              <TableCell align="right">{issuedQty}</TableCell>
                              <TableCell align="right">{receivedQty}</TableCell>
                              <TableCell align="right" sx={{ bgcolor: 'warning.light' }}>{closedShort}</TableCell>
                              <TableCell align="right">{pending}</TableCell>
                            </TableRow>
                          );
                        })}
                        <TableRow>
                          <TableCell><strong>Total</strong></TableCell>
                          <TableCell align="right"><strong>{totalProgram}</strong></TableCell>
                          <TableCell align="right"><strong>{totalIssued}</strong></TableCell>
                          <TableCell align="right"><strong>{totalReceived}</strong></TableCell>
                          <TableCell align="right" sx={{ bgcolor: 'warning.light' }}>
                            <strong>
                              {sizes.reduce((sum, sz) => sum + (shortfallSummary[`${part}|${sz}`]?.closed || 0), 0)}
                            </strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>
                              {Math.max(0, totalProgram - totalReceived - sizes.reduce((sum, sz) => sum + (shortfallSummary[`${part}|${sz}`]?.closed || 0), 0))}
                            </strong>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Paper>
                </Grid>
              );
            })}

            {/* Job Works */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Job Works</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Code</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Vendor</TableCell>
                      <TableCell>Issue Date</TableCell>
                      <TableCell>Actual Return</TableCell>
                      <TableCell align="right">Total Completed</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(jobWorks || []).map((jw: any) => {
                      const completed = (jw.receivedItems || []).reduce((s: number, ri: any) => s + (ri.pieceCount || 0), 0);
                      const vendorName = jw.vendor?.alias || jw.vendor?.registered_name || jw.jobWorkerName || '-';
                      const issue = jw.issueDate || jw.jobWorkDate;
                      return (
                        <TableRow key={jw.jobWorkCode}>
                          <TableCell>{jw.jobWorkCode}</TableCell>
                          <TableCell>{jw.status}</TableCell>
                          <TableCell>{vendorName}</TableCell>
                          <TableCell>{issue ? new Date(issue).toLocaleDateString() : '-'}</TableCell>
                          <TableCell>{jw.actualReturn ? new Date(jw.actualReturn).toLocaleDateString() : '-'}</TableCell>
                          <TableCell align="right">{completed}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>

            {/* Materials */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Job Work Materials</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Job Work</TableCell>
                      <TableCell>Material Code</TableCell>
                      <TableCell>Quality</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell>UOM</TableCell>
                      <TableCell align="right">Rate</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(jobWorks || []).flatMap((jw: any) => (jw.materials || []).map((m: any, idx: number) => (
                      <TableRow key={`${jw.jobWorkCode}-${idx}`}>
                        <TableCell>{jw.jobWorkCode}</TableCell>
                        <TableCell>{m.material?.material_code || '-'}</TableCell>
                        <TableCell>{m.material?.material_quality || '-'}</TableCell>
                        <TableCell align="right">{m.quantity}</TableCell>
                        <TableCell>{m.uom}</TableCell>
                        <TableCell align="right">{m.rate || '-'}</TableCell>
                      </TableRow>
                    )))}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Design Cutting Record Dialog */}
      <DesignCuttingRecordDialog
        open={showCuttingRecordDialog}
        designNo={designNo}
        onClose={() => setShowCuttingRecordDialog(false)}
      />
    </Dialog>
  );
};

export default DispatchedDetailsDialog;


